import { createSlice } from "@reduxjs/toolkit";
import { SortDirection } from "@episerver/ui-framework";
import { ConfirmDialog, Contact, ContactsType } from "../model/contact";
import { fetchContacts, searchContacts, deleteContacts } from "./asyncThunks";

interface SortModel {
    sortedColumn: string;
    sortDirection: SortDirection;
}

interface PaginationModel {
    startRow: number;
    totalRowCount: number;
    rowsPerPage: number;
    rowsPerPageOptions: number[];
    searchInput: string;
}

export interface ContactManagerState {
    contacts: Contact[];
    totalContacts: number;
    selectedContacts: string[];
    sort: SortModel;
    contactListPagination: PaginationModel;
    confirmDialog: ConfirmDialog;
}

export const initialContactState: ContactManagerState = {
    contacts: [] as Contact[],
    totalContacts: 0,
    selectedContacts: [] as string[],
    sort: {
        sortedColumn: "FullName",
        sortDirection: SortDirection.Ascending,
    },
    contactListPagination: {
        startRow: 0,
        rowsPerPage: 10,
        rowsPerPageOptions: [10, 20, 50, 100],
        totalRowCount: 0,
        searchInput: "",
    },
    confirmDialog: {
        open: false,
        title: "Delete",
        message: "",
    } as ConfirmDialog
};

export const contactListSlice = createSlice({
    name: "contactList",
    initialState: initialContactState,
    reducers: {
        selectContact: (state, action) => {
            state.selectedContacts.push(action.payload);
        },
        deselectContact: (state, action) => {
            state.selectedContacts = state.selectedContacts.filter(
                (PrimaryKeyId: string) => PrimaryKeyId !== action.payload
            );
        },
        selectAllContacts: (state) => {
            state.selectedContacts = state.selectedContacts
                .concat(state.contacts.map((contact: Contact) => contact.primaryKeyId!))
                .filter(function (elem, index, self) {
                    return index === self.indexOf(elem);
                });
        },
        deselectAllContacts: (state) => {
            state.selectedContacts = state.selectedContacts.filter(function (el) {
                return !state.contacts.map((contact: Contact) => contact.primaryKeyId!).includes(el);
            });
        },
        sort: (state, action): void => {
            const { column, direction } = action.payload;
            state.sort = { sortDirection: direction, sortedColumn: column };
        },
        onChangeConfirmDialog: (state, action): void => {
            state.confirmDialog = action.payload;
        },
        reset: (state): void => {
            state.selectedContacts = initialContactState.selectedContacts;
        },
    },
    extraReducers: {
        [`${fetchContacts.fulfilled}`]: (state, action): void => {
            state.contacts = action.payload.contacts;
            state.contactListPagination = {
                ...state.contactListPagination,
                ...action.payload.pagingInfo,
            };
        },
        [`${searchContacts.fulfilled}`]: (state, action): void => {
            state.contacts = action.payload.contacts;
            state.contactListPagination = {
                ...state.contactListPagination,
                ...action.payload.pagingInfo,
            };
        },
        [`${deleteContacts.fulfilled}`]: (state, action): void => {
            state.contacts = action.payload.contacts;
            state.contactListPagination = {
                ...state.contactListPagination,
                ...action.payload.pagingInfo,
            };
            state.selectedContacts = [];
        },
    },
});

// Actions
export const {
    onChangeConfirmDialog,
    selectContact,
    deselectContact,
    selectAllContacts,
    deselectAllContacts,
    sort,
    reset
} = contactListSlice.actions;
